import { ButtonHTMLAttributes, SVGAttributes, useState } from 'react';
import { useEffect } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/router';

import { faApple, faFacebook, faGooglePlay, faInstagram } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  ArrowIconWithDir,
  CheckIcon,
  EmailIconNew,
  GitHubIcon,
  Logo,
  OpenNewTabIcon,
  TelegramIconNew,
  TwitterIconNew,
  YoutubeIconNew,
} from '@/components/icons/ElementIcons';
import ExportedImage from '@/components/shared/ExportedImage';
import { GleecDomainDisclaimer } from '@/components/shared/GleecDomainDisclaimer';
import { FooterDropdown } from '@/components/shared/lp/FooterDropdown';

import { gleecSocialLinks } from '@/data/configs/gleecSocialLinks';
// import NewsletterSubscriptionForm from '@/components/shared/lp/NewsletterSubscriptionForm';
import pagesWithMissingFooter from '@/data/configs/missingFooter.json';
import { siteData } from '@/data/configs/site';
import fileData from '@/data/docs/_fileData.json';
import { humanizeTime } from '@/shared-functions/timeAgo';

import footerArrow from '/public/images/lp/komodo-wallet/footer-arrow.svg';

type FooterItem = {
  href: string;
  text: string;
  arrow: boolean;
  icon?: React.ReactNode;
  shortText: string;
};

const FooterDropdownData: Array<{ headerNo: number; header: string; footerItems: FooterItem[] }> = [
  {
    headerNo: 1,
    header: 'Social Media',
    footerItems: [
      {
        href: siteData.social.twitter,
        text: 'X.com',
        arrow: false,
        shortText: 'twitter',
        icon: <TwitterIconNew className='mr-3 h-auto w-[19px]' />,
      },
      {
        href: gleecSocialLinks.telegram,
        text: 'Telegram',
        arrow: false,
        icon: <TelegramIconNew className='mr-3 h-auto w-[19px]' />,
        shortText: 'telegram',
      },
      {
        href: gleecSocialLinks.youtube,
        text: 'Youtube',
        arrow: false,
        icon: <YoutubeIconNew className='mr-3 h-auto w-[19px]' />,
        shortText: 'youtube',
      },
      {
        href: `mailto:${siteData.helloEmail}`,
        text: 'Email',
        arrow: false,
        icon: <EmailIconNew className='mr-3 h-auto w-[19px]' />,
        shortText: 'email',
      },
      {
        href: gleecSocialLinks.facebook,
        text: 'Facebook',
        arrow: false,
        icon: <FontAwesomeIcon icon={faFacebook} className='mr-3 h-auto w-[19px]' />,
        shortText: 'facebook',
      },
      {
        href: gleecSocialLinks.instagram,
        text: 'Instagram',
        arrow: false,
        icon: <FontAwesomeIcon icon={faInstagram} className='mr-3 h-auto w-[19px]' />,
        shortText: 'instagram',
      },
    ],
  },
  {
    headerNo: 2,
    header: 'Products',
    footerItems: [
      {
        href: 'https://dex.gleec.com/wallet',
        text: 'Gleec Wallet',
        arrow: true,
        shortText: 'gleecWallet',
      },
      { href: 'https://gleecpay.com/', text: 'Gleec Pay', arrow: true, shortText: 'gleecPay' },
      { href: 'https://gleechat.com/', text: 'Gleec Chat', arrow: true, shortText: 'gleecChat' },
      { href: 'https://gleec.com/card', text: 'Gleec Card', arrow: true, shortText: 'gleecCard' },
      { href: 'https://gleec.com/coin', text: 'Gleec Coin', arrow: true, shortText: 'gleecCoin' },
      {
        href: 'https://exchange.gleec.com/',
        text: 'Gleec Exchange',
        arrow: true,
        shortText: 'gleecExchange',
      },
    ],
  },
  {
    headerNo: 3,
    header: 'Developers',
    footerItems: [
      {
        href: 'https://github.com/GLEECBTC/komodo-defi-framework',
        text: 'Komodo DeFi Framework',
        arrow: true,
        shortText: 'komodoDefiFramework',
      },
      {
        href: 'https://github.com/GLEECBTC/gleec-wallet',
        text: 'GUI repo',
        arrow: true,
        shortText: 'guiRepo',
      },
      {
        href: '#',
        text: 'Gleec',
        arrow: false,
        shortText: 'komodo',
      },
    ],
  },
  {
    headerNo: 4,
    header: 'About',
    footerItems: [
      { href: '#', text: 'Privacy Policy', arrow: false, shortText: 'privacyPolicy' },
      { href: '#', text: 'Disclaimer', arrow: false, shortText: 'disclaimer' },
      { href: '/contact/', text: 'Contact', arrow: false, shortText: 'contact' },
    ],
  },
];

// Retained for the commented-out mobile assets menu.
/*
const FooterDropdownDataRow2: Array<{
  headerNo: number;
  header: string;
  footerItems: FooterItem[];
}> = [
  {
    headerNo: 1,
    header: 'Supported Assets',
    footerItems: [
      {
        href: '/wallet/supported-assets/',
        text: 'Supported Assets',
        arrow: false,
        shortText: 'supportedAssets',
      },
      {
        href: 'https://markets.komodoplatform.com/?netid=ALL',
        text: 'DEX Markets/Stats',
        arrow: true,
        shortText: 'stats',
      },
    ],
  },
];
*/

const AppStoreLinks = () => (
  <div className='mt-4 flex items-center gap-4'>
    <Link
      href='https://apps.apple.com/am/app/gleecdex-wallet/id1563795528'
      aria-label='Download Gleec Wallet on the App Store'
      title='App Store'
      className='flex h-10 w-10 items-center justify-center'
    >
      <FontAwesomeIcon icon={faApple} style={{ width: 28, height: 28 }} />
    </Link>
    <Link
      href='https://play.google.com/store/apps/details?id=com.gleec.gleecdex'
      aria-label='Download Gleec Wallet on Google Play'
      title='Google Play'
      className='flex h-10 w-10 items-center justify-center'
    >
      <FontAwesomeIcon icon={faGooglePlay} style={{ width: 28, height: 28 }} />
    </Link>
  </div>
);

export const LpFooter = () => {
  const [selected, setSelected] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    setIsMobile(window.innerWidth < 1024);
  }, []);

  const router = useRouter();
  return (
    <footer
      className={`container relative mx-auto flex flex-col items-center overflow-hidden pt-[60px] font-sans text-white ${pagesWithMissingFooter.some((page) => router.asPath.includes(page)) ? 'hidden' : 'lg:grid lg:grid-cols-24 lg:gap-0'}`}
    >
      <div className='hidden lg:col-span-1 lg:block xl:col-span-2'></div>
      <div className='relative hidden justify-between px-4 xs:px-8 md:col-span-24 lg:col-span-22 lg:block lg:px-0 xl:col-span-20'>
        <div className='relative flex items-center justify-between md:grid md:grid-cols-24 md:justify-center md:gap-0 lg:justify-between'>
          <div className='text-white lg:col-span-6 xlm:col-span-7'>
            <div className='w-[232px]'>
              <div className='flex w-full justify-between md:w-auto'>
                <Link href='/' aria-label='Gleec logo' className=''>
                  <Logo />
                </Link>
              </div>
            </div>
          </div>
          <div className='lg:col-span-4 xlm:col-span-4'>
            <div className='w-[170px]'>
              <h3 className='leading-1 font-heading text-base font-semibold text-br-footer-gray lg:text-lg'>
                Social Media
              </h3>
            </div>
          </div>
          <div className='lg:col-span-5 xlm:col-span-5'>
            <div className='w-[168px]'>
              <h3 className='leading-1 font-heading text-base font-semibold text-br-footer-gray lg:text-lg'>
                Products
              </h3>
            </div>
          </div>
          <div className='lg:col-span-6 xlm:col-span-5'>
            <div className='w-[207px]'>
              <h3 className='leading-1 font-heading text-base font-semibold text-br-footer-gray lg:text-lg'>
                Developers
              </h3>
            </div>
          </div>
          <div className='lg:col-span-3 xlm:col-span-3'>
            <div className='w-[107px]'>
              <h3 className='leading-1 font-heading text-base font-semibold text-br-footer-gray lg:text-lg'>
                About
              </h3>
            </div>
          </div>
        </div>
        <div className='relative lg:grid lg:grid-cols-24 lg:gap-0'>
          <div className='lg:col-span-6 xlm:col-span-7'>
            <div className='mt-[32px] border-r-[2px] border-[#3C3C3C] lg:w-[222px] xl:w-[240px] 2xl:w-[280px]'>
              {/* <h3 className='font-semibold text-start font-heading lg:text-base'>
                Subscribe to newsletter
              </h3>
             <Link
                href='https://dashboard.mailerlite.com/forms/20792/52187545986925609/share'
                className='mb-4 text-start text-base font-normal leading-[21.83px] text-blue-500 underline lg:text-sm'
                target='_blank'
                rel='noreferrer'
              >
                Receive the latest news
                <br /> from Komodo
              </Link> */}
              {/* {!isMobile && <NewsletterSubscriptionForm device='desktop' />} */}
            </div>
          </div>
          <div className='text-white lg:col-span-4 xlm:col-span-4'>
            <div className='w-[170px]'>
              <ul role='list' className='mt-2 space-y-[8px]'>
                {FooterDropdownData[0].footerItems.map((item, index) => (
                  <li className='flex space-x-3' key={index}>
                    <Link href={item.href} className={`desk-footer-${item.shortText}`}>
                      <div className='flex'>
                        {item.icon}
                        <span className='leading-1 text-sm font-medium lg:text-base'>
                          {item.text}
                        </span>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className='text-white lg:col-span-5 xlm:col-span-5'>
            <div className='w-[168px]'>
              <ul role='list' className='mt-2 space-y-[8px]'>
                {FooterDropdownData[1].footerItems.map((item, index) => (
                  <li className='flex space-x-3' key={index}>
                    <Link href={item.href} className={`desk-footer-${item.shortText}`}>
                      <span className='leading-1 text-base font-medium text-[#F4F4F4]'>
                        {item.text}{' '}
                        {item.arrow && (
                          <ExportedImage
                            placeholder='empty'
                            src={footerArrow}
                            className='inline h-auto w-[8px] align-middle'
                            alt='footer-arrow'
                          />
                        )}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
              <AppStoreLinks />
            </div>
          </div>
          <div className='text-white lg:col-span-6 xlm:col-span-5'>
            <div className='w-[207px]'>
              <ul role='list' className='mt-2 space-y-[8px]'>
                {FooterDropdownData[2].footerItems.map((item, index) => (
                  <li key={index} className='flex space-x-3'>
                    <Link
                      href={item.href}
                      target={item.href === '#' ? undefined : '_blank'}
                      rel={item.href === '#' ? undefined : 'noreferrer'}
                    >
                      <span className='leading-1 text-sm font-medium text-[#F4F4F4] lg:text-base'>
                        {item.text}{' '}
                        {item.arrow && (
                          <ExportedImage
                            placeholder='empty'
                            src={footerArrow}
                            className='inline h-auto w-[8px] align-middle'
                            alt='footer-arrow'
                          />
                        )}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className='text-white lg:col-span-3 xlm:col-span-3'>
            <div className='w-[170px]'>
              <ul role='list' className='mt-2 space-y-[8px]'>
                {FooterDropdownData[3].footerItems.map((item, index) => (
                  <li key={index} className='flex space-x-3'>
                    <Link href={item.href}>
                      <span
                        className={`leading-1 text-sm font-medium ${!item.arrow ? 'underline' : ''} lg:text-base`}
                      >
                        {item.text}{' '}
                        {item.arrow && (
                          <ExportedImage
                            placeholder='empty'
                            src={footerArrow}
                            className='inline h-auto w-[8px] align-middle'
                            alt='footer-arrow'
                          />
                        )}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div className='footer-lg-gray relative flex flex-col items-center justify-center pb-[60px]'>
          <p className='footer-komodo pt-12 text-left font-heading text-sm font-normal text-white lg:text-base'>
            &copy; 2016 - {new Date().getFullYear()} by&nbsp;
            <span className='font-semibold'>Komodo Platform</span>
            <span className='ml-4 inline-block whitespace-nowrap'>
              &copy; {new Date().getFullYear()} by <span className='font-semibold'>Gleec</span>
            </span>
          </p>
          <GleecDomainDisclaimer />
        </div>
      </div>
      <div className='container mx-auto block overflow-hidden py-9 text-white lg:hidden'>
        <div className='relative mx-auto flex max-w-[280px] flex-wrap items-center justify-between xs:max-w-[343px]'>
          {FooterDropdownData[0].footerItems.map((item) => (
            <Link
              key={item.shortText}
              href={item.href}
              aria-label={item.text}
              className={`mobi-footer-${item.shortText} flex h-10 w-10 items-center justify-center`}
            >
              <span className='[&>svg]:m-0 [&>svg]:h-8 [&>svg]:w-8'>{item.icon}</span>
            </Link>
          ))}
        </div>
        <div className='mx-auto flex max-w-[280px] flex-wrap justify-center pt-6 xs:w-full xs:max-w-[343px]'>
          <div className='w-full'>
            {FooterDropdownData.map((item, index) => (
              <FooterDropdown
                key={index}
                headerNo={item.headerNo}
                header={item.header}
                footerItems={item.footerItems}
                selected={selected}
                setSelected={setSelected}
              >
                {item.header === 'Products' && <AppStoreLinks />}
              </FooterDropdown>
            ))}
            {/* Temporarily hidden: Supported Assets and DEX Markets/Stats.
            {FooterDropdownDataRow2.map((item, index) => (
              <FooterDropdown
                key={`row2-${index}`}
                headerNo={item.headerNo + 10} // Offset to avoid conflicts with existing headerNo
                header={item.header}
                footerItems={item.footerItems}
                selected={selected}
                setSelected={setSelected}
              />
            ))} */}
          </div>
        </div>

        <div className='relative mx-auto flex max-w-[280px] flex-wrap justify-center py-8 xs:w-full xs:max-w-[300px]'>
          <Link href='https://dex.gleec.com' className='mobi-footer-web'>
            <span className='text-base font-medium'>
              Gleec Wallet (web){' '}
              <ExportedImage
                placeholder='empty'
                src={footerArrow}
                className='inline h-auto w-[8px] align-middle'
                alt='footer-arrow'
              />
            </span>
          </Link>
        </div>
        <div className='relative mx-auto flex w-[280px] flex-col items-center justify-center pt-6 xs:w-[343px]'>
          {/* <h3 className='text-center text-[24px] font-bold'>Subscribe to newsletter</h3>
         <Link
            href='https://dashboard.mailerlite.com/forms/20792/52187545986925609/share'
            className='mx-auto text-sm font-normal text-center text-blue-500 underline'
            target='_blank'
            rel='noreferrer'
          >
            Receive the latest news from Komodo
          </Link> */}
          {/* {isMobile && <NewsletterSubscriptionForm device='mobile' />} */}
        </div>
        <div className='relative mx-auto flex w-[280px] flex-row items-end justify-center pt-12 xs:w-[343px]'>
          <Link href='/' aria-label='Gleec logo' className=''>
            <Logo />
          </Link>
        </div>
        <div className='footer-sm-gray relative mx-auto flex flex-col justify-between pt-6'>
          <p className='footer-komodo pt-8 text-center text-sm font-medium sm:text-center xl:w-full'>
            © 2016 - {new Date().getFullYear()} by&nbsp;
            <span className='font-semibold'>Komodo Platform</span>
            <span className='ml-4 inline-block whitespace-nowrap'>
              &copy; {new Date().getFullYear()} by <span className='font-semibold'>Gleec</span>
            </span>
          </p>
          <GleecDomainDisclaimer />
          {/* <p className='pt-8 text-sm font-medium text-center xl:text-base footer-komodo'>
            <Link href='/privacy'>
              <span className='pt-6 text-sm font-medium text-center lg:text-base footer-komodo rounded-none border-b pb-0.5'>
                Privacy Policy
              </span>
            </Link>
          </p>
          <p className='pt-8 text-sm font-medium text-center xl:text-base footer-komodo'>
            <Link href='/tos/'>
              <span className='pt-6 text-sm font-medium text-center xl:text-base footer-komodo rounded-none border-b pb-0.5'>
                Disclaimer
              </span>
            </Link>
          </p> */}
        </div>
      </div>
      <div className='hidden lg:col-span-1 lg:block xl:col-span-2'></div>
    </footer>
  );
};
